import * as React from "react";
import { useQuery } from "react-query";
import { Typography, Box, Stack, TablePagination } from "@mui/material";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ExportButton } from "components/ExportButton/ExportButton";
import { MetricsService } from "gen/clients";
import { default as dayjs } from "dayjs";

const MT_DOC_TRANSLATION_EVENT_TYPE = "MT_DOC_TRANSLATION_REQUESTED";
const MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_DOC_ESCALATION_TO_HUMAN";
const MT_MEDIA_TRANSLATION_EVENT_TYPE = "MT_MEDIA_TRANSLATION_REQUESTED";
const MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_MEDIA_ESCALATION_TO_HUMAN";

interface EscalationTableRow {
  username: string;
  docReq: number;
  docEsc: number;
  mediaReq: number;
  mediaEsc: number;

  [key: string]: string | number;
}

interface ExportedDataRow {
  username: string;
  docReq: number;
  docEsc: number;
  mediaReq: number;
  mediaEsc: number;

  [key: string]: string | number;
}

const columnHeaders: Record<keyof ExportedDataRow, string> = {
  username: "Username",
  docReq: "Request Count (Documents)",
  docEsc: "Escalation Count (Documents)",
  mediaReq: "Request Count (Multimedia)",
  mediaEsc: "Escalation Count (Multimedia)"
};

const buildQuery = (fromISO: string, toISO: string, clientId: string): Record<string, any> => ({
  size: 0,
  query: {
    bool: {
      filter: [
        {
          range: {
            "@timestamp": {
              gte: fromISO,
              lt: toISO
            }
          }
        },
        {
          term: {
            clientId
          }
        },
        {
          terms: {
            eventType: [
              MT_DOC_TRANSLATION_EVENT_TYPE,
              MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE,
              MT_MEDIA_TRANSLATION_EVENT_TYPE,
              MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE
            ]
          }
        }
      ]
    }
  },
  aggs: {
    by_username: {
      terms: {
        field: "triggeredByUsername",
        size: 10000
      },
      aggs: {
        by_type: {
          filters: {
            filters: {
              doc_req: {
                term: {
                  eventType: MT_DOC_TRANSLATION_EVENT_TYPE
                }
              },
              doc_esc: {
                term: {
                  eventType: MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE
                }
              },
              media_req: {
                term: {
                  eventType: MT_MEDIA_TRANSLATION_EVENT_TYPE
                }
              },
              media_esc: {
                term: {
                  eventType: MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE
                }
              }
            }
          }
        }
      }
    }
  }
});

const parseResponse = (response: any): EscalationTableRow[] => {
  const usernameBuckets = response?.aggregations?.by_username?.buckets || [];

  const rows = usernameBuckets.map((usernameBucket: any) => {
    const username = usernameBucket.key;
    const typeBuckets = usernameBucket.by_type?.buckets || {};

    return {
      username,
      docReq: typeBuckets.doc_req?.doc_count || 0,
      docEsc: typeBuckets.doc_esc?.doc_count || 0,
      mediaReq: typeBuckets.media_req?.doc_count || 0,
      mediaEsc: typeBuckets.media_esc?.doc_count || 0
    };
  });

  // Sort by total escalations descending
  return rows.sort((a: EscalationTableRow, b: EscalationTableRow) => b.docEsc + b.mediaEsc - (a.docEsc + a.mediaEsc));
};

interface Props {
  from: Date;
  to: Date;
  clientId: string;
  refetchRef?: React.MutableRefObject<() => void>;
}

const EscalationByContactDetailsTable: React.FC<Props> = ({ from, to, clientId, refetchRef }) => {
  const fromISO = React.useMemo(() => from.toISOString(), [from]);
  const toISO = React.useMemo(() => to.toISOString(), [to]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { data, isLoading, error, refetch } = useQuery(
    ["mt-escalation-by-contact-table", fromISO, toISO, clientId],
    () =>
      MetricsService.searchIndex({
        indexName: "app-events-v2",
        requestBody: buildQuery(fromISO, toISO, clientId)
      }),
    {
      cacheTime: 0,
      enabled: !!clientId
    }
  );

  React.useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetch, refetchRef]);

  const rows = React.useMemo(() => parseResponse(data), [data]);

  const pageData = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return rows.slice(startIndex, endIndex);
  }, [rows, page, rowsPerPage]);

  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    return rows;
  }, [rows]);

  const fileName = `mt-escalations-by-contact-${dayjs(from).format("MM-DD-YYYY")}-${dayjs(to).format("MM-DD-YYYY")}.csv`;

  if (!clientId) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2
        }}
      >
        <Typography variant="h6">Escalation Details by Client Contact</Typography>
        <ExportButton<ExportedDataRow>
          filename={fileName}
          columnHeaders={columnHeaders}
          getExportData={getExportData}
          exportingMessage="Exporting escalation data..."
        />
      </Box>

      {error ? (
        <ApiErrorMessage apiError={error} />
      ) : (
        <Stack>
          <DataTable
            columns={[
              {
                id: "username",
                title: "Username",
                cell: (row: EscalationTableRow) => row.username
              },
              {
                id: "docReq",
                title: "Request Count (Documents)",
                cell: (row: EscalationTableRow) => row.docReq
              },
              {
                id: "docEsc",
                title: "Escalation Count (Documents)",
                cell: (row: EscalationTableRow) => row.docEsc
              },
              {
                id: "mediaReq",
                title: "Request Count (Multimedia)",
                cell: (row: EscalationTableRow) => row.mediaReq
              },
              {
                id: "mediaEsc",
                title: "Escalation Count (Multimedia)",
                cell: (row: EscalationTableRow) => row.mediaEsc
              }
            ]}
            data={pageData}
            rowKey={(row: EscalationTableRow) => row.username}
            isLoading={isLoading}
            noData={<Typography>No escalation data found for the selected date range.</Typography>}
          />
          <TablePagination
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Stack>
      )}
    </Box>
  );
};

export { EscalationByContactDetailsTable };
